/**
 * DailyHoursChart — task 157 D4 production chart.
 *
 * Org-wide hours per day across the active date range, stacked into
 * billable vs non-billable. Sits above the employee table on the
 * Dashboard page; data comes from `useDashboardDailyStats()`. When
 * the active range produces no rows, the composite renders an
 * EmptyState in place of the chart.
 *
 * Why a composite, not inlined: the chart owns a non-trivial amount
 * of option-shape logic (axis labels, tooltip formatter, stack keys)
 * and a unit test covers it independently of the page. Keeping it
 * separate keeps DashboardPage.tsx readable.
 */

import { useMemo } from 'react';
import { Chart, useChartTheme } from '@/components/primitives/Chart';
import { EmptyState } from '@/components/primitives/EmptyState';
import { formatDayShort } from '@/lib/dates';
import type { DailyStat } from '@/data/dashboardDailyStats';
import styles from './DailyHoursChart.module.css';

interface DailyHoursChartProps {
  days: readonly DailyStat[];
}

const CHART_HEIGHT = 280;

export function DailyHoursChart({ days }: DailyHoursChartProps) {
  const theme = useChartTheme();

  const option = useMemo(() => {
    const dates = days.map((d) => d.date);
    const billable = days.map((d) => d.billableHours);
    const nonBillable = days.map((d) => d.nonBillableHours);

    return {
      // Tooltip on the time axis — `axis` mode aligns to the hovered
      // category and shows both stacked series at once.
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        valueFormatter: (v: number) => `${v.toFixed(1)}h`,
      },
      legend: {
        data: ['Billable', 'Non-billable'],
        top: 8,
        textStyle: { color: theme.text },
        // Use the theme palette explicitly so the legend swatches
        // match the bar fills rather than echart's default rotation.
        itemWidth: 12,
        itemHeight: 12,
      },
      grid: {
        // Override the primitive's default top padding to fit the
        // legend, which we render at top:8.
        top: 40,
      },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: {
          color: theme.textMuted,
          // Render ISO dates as "Apr 13" — keeps the axis tidy across
          // a 30-day window without overlapping ticks.
          formatter: (value: string) => formatDayShort(`${value}T12:00:00.000Z`),
        },
        axisLine: { lineStyle: { color: theme.border } },
        axisTick: { lineStyle: { color: theme.border } },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: theme.textMuted,
          formatter: (v: number) => `${v}h`,
        },
        splitLine: { lineStyle: { color: theme.border, type: 'dashed' } },
      },
      series: [
        {
          name: 'Billable',
          type: 'bar',
          stack: 'hours',
          data: billable,
          itemStyle: { color: theme.colors[0] },
          emphasis: { focus: 'series' },
        },
        {
          name: 'Non-billable',
          type: 'bar',
          stack: 'hours',
          data: nonBillable,
          itemStyle: { color: theme.colors[1] },
          emphasis: { focus: 'series' },
        },
      ],
    };
  }, [days, theme]);

  if (days.length === 0) {
    return (
      <div className={styles.empty}>
        <EmptyState
          title="No hours in this range"
          description="Pick a different date range to see daily activity."
        />
      </div>
    );
  }

  // Build a screen-reader-friendly summary so axe (and human users)
  // get more than the bare aria-label.
  const totalBillable = days.reduce((s, d) => s + d.billableHours, 0);
  const totalNonBillable = days.reduce((s, d) => s + d.nonBillableHours, 0);
  const fallback =
    `Daily hours from ${days[0].date} to ${days[days.length - 1].date}: ` +
    `${totalBillable.toFixed(1)}h billable, ${totalNonBillable.toFixed(1)}h non-billable across ${days.length} days.`;

  return (
    <Chart
      option={option}
      height={CHART_HEIGHT}
      ariaLabel="Daily hours, billable versus non-billable"
      textFallback={fallback}
    />
  );
}
